// Explains WHY a lazy screen chunk failed to load (ChunkLoadTimeout /
// ChunkLoadError), for the frontend activity log.
//
// The recovery-screen entry used to say only "ChunkLoadTimeout" plus a React
// component stack, which cannot tell apart the causes that actually happen:
//
//   - the file is still downloading (slow / stalled network, proxy, antivirus)
//   - the server answered with an error (e.g. 403/404 after a deploy)
//   - a newer build was deployed, so the old chunk URL no longer exists
//   - the browser was offline or the site was unreachable
//   - the 20 s timer fired late because the tab was in the background or the
//     computer was asleep (not a network problem at all)
//   - the chunk queued behind the background route prefetch
//
// `diagnoseChunkFailure` gathers the evidence for those (resource timing of the
// chunk files, pending <script>/<link> tags, a no-store probe of
// /asset-manifest.json, tab visibility, connection info) and returns flat fields
// plus a plain-English `diagnosis` block. The block is a multi-line string on
// purpose: the log sink prints string details line by line, while an object
// would be squashed into one JSON line.
//
// Never throws, and bounded to a few seconds, so it can run on the failure path
// before the reload / error boundary.

const PROBE_TIMEOUT_MS = 4000;
const PREVIOUS_ATTEMPT_KEY = 'chunk_fail_prev';
// The retry guard only lets a second failure through within ~30 s of the
// reload; anything much older belongs to an earlier, recovered episode.
const PREVIOUS_ATTEMPT_MAX_AGE_MS = 5 * 60 * 1000;

// ── Tab visibility history ──────────────────────────────────────────────────
// Background tabs throttle timers and network, and a sleeping computer freezes
// them entirely; either can make the 20 s timer fire with nothing wrong on the
// network. Track the most recent hidden interval to tell.
let hiddenSince: number | null =
  typeof document !== 'undefined' && document.hidden ? Date.now() : null;
let lastHiddenEnd = 0;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenSince = Date.now();
    } else {
      lastHiddenEnd = Date.now();
      hiddenSince = null;
    }
  });
}
const wasHiddenSince = (startedAt: number) =>
  hiddenSince !== null || lastHiddenEnd > startedAt;

// ── Helpers ─────────────────────────────────────────────────────────────────
const secs = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
const kb = (bytes: number) => `${Math.round(bytes / 1024)} KB`;
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Webpack chunk ids a lazy factory loads, read from its source.
 *  Production: `()=>Promise.all([n.e(1134),n.e(106)]).then(...)` → ["1134","106"].
 *  Development: `__webpack_require__.e("src_content_..._tsx")`. */
const chunkIdsOf = (factory: () => unknown): string[] => {
  try {
    const src = String(factory);
    const ids = new Set<string>();
    const re = /\.e\(\s*(?:\/\*[\s\S]*?\*\/\s*)?["']?([\w.-]+)["']?\s*\)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(src))) ids.add(m[1]);
    return Array.from(ids);
  } catch {
    return [];
  }
};

const chunkFileRe = (id: string, kind: 'js' | 'css') =>
  new RegExp(`/static/${kind}/${escapeRe(id)}\\.[^/]*chunk\\.${kind}(\\?|$)`);

type ChunkFileState = {
  id: string;
  kind: 'js' | 'css';
  state: 'done' | 'failed' | 'pending' | 'not-requested';
  line: string;
};

const describeChunkFile = (
  id: string,
  kind: 'js' | 'css',
  startedAt: number
): ChunkFileState | null => {
  const re = chunkFileRe(id, kind);
  const navStart = performance.timeOrigin || Date.now() - performance.now();
  const entries = (performance.getEntriesByType('resource') as PerformanceResourceTiming[]).filter(
    (e) => re.test(e.name)
  );
  const entry = entries[entries.length - 1];
  // Webpack keeps a chunk's <script> in the DOM only while it is loading; a
  // CSS chunk's <link> stays, so for CSS "pending" means a link with no
  // completed resource-timing entry.
  const tag =
    kind === 'js'
      ? Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]')).find((s) =>
          re.test(s.src)
        )
      : Array.from(
          document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')
        ).find((l) => re.test(l.href));

  if (entry) {
    const startedRel = navStart + entry.startTime - startedAt;
    const status = (entry as any).responseStatus as number | undefined;
    const fromCache = entry.transferSize === 0 && entry.decodedBodySize > 0;
    const failed = (status !== undefined && status >= 400) || entry.decodedBodySize === 0;
    const parts = [
      failed ? 'FAILED' : 'downloaded',
      `in ${secs(entry.duration)}`,
      fromCache ? 'from cache' : entry.transferSize ? kb(entry.transferSize) : null,
      status ? `HTTP ${status}` : null,
      startedRel < -500
        ? `(request started ${secs(-startedRel)} before this screen asked for it — background prefetch)`
        : null
    ].filter(Boolean);
    return { id, kind, state: failed ? 'failed' : 'done', line: `${id}.${kind}: ${parts.join(', ')}` };
  }
  if (tag) {
    return {
      id,
      kind,
      state: 'pending',
      line: `${id}.${kind}: STILL DOWNLOADING — requested, no response after ${secs(
        Date.now() - startedAt
      )}`
    };
  }
  // Only JS is always present; most chunks have no CSS file at all.
  if (kind === 'css') return null;
  return { id, kind, state: 'not-requested', line: `${id}.js: never requested by the browser` };
};

type ManifestProbe = {
  ok: boolean;
  line: string;
  status?: number;
  ms?: number;
  deployChanged?: boolean;
  missingChunks?: string[];
};

const probeManifest = async (chunkIds: string[]): Promise<ManifestProbe> => {
  const started = Date.now();
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`/asset-manifest.json?probe=${started}`, {
      cache: 'no-store',
      signal: controller?.signal
    });
    const ms = Date.now() - started;
    if (!res.ok) {
      return { ok: false, status: res.status, ms, line: `site check: /asset-manifest.json answered HTTP ${res.status} in ${ms} ms` };
    }
    const manifest = await res.json();
    const files: Record<string, string> = manifest?.files || {};
    const liveMain = String(files['main.js'] || '').split('/').pop() || '?';
    const ourMain =
      (
        Array.from(document.querySelectorAll<HTMLScriptElement>('script[src*="/static/js/main."]'))[0]
          ?.src || ''
      )
        .split('/')
        .pop() || '?';
    const deployChanged = liveMain !== '?' && ourMain !== '?' && liveMain !== ourMain;
    const keys = Object.keys(files);
    const missingChunks = chunkIds.filter(
      (id) => !keys.some((k) => chunkFileRe(id, 'js').test(`/${k}`))
    );
    const build = deployChanged
      ? `NEW BUILD DEPLOYED since this page loaded (page has ${ourMain}, server has ${liveMain})`
      : `same build (${ourMain})`;
    const missing = missingChunks.length
      ? `; chunk(s) ${missingChunks.join(', ')} NOT in the live build`
      : '';
    return {
      ok: true,
      status: res.status,
      ms,
      deployChanged,
      missingChunks,
      line: `site check: /asset-manifest.json answered HTTP ${res.status} in ${ms} ms; ${build}${missing}`
    };
  } catch (e: any) {
    const ms = Date.now() - started;
    const why = e?.name === 'AbortError' ? `no answer within ${secs(PROBE_TIMEOUT_MS)}` : String(e?.message || e);
    return { ok: false, ms, line: `site check: /asset-manifest.json FAILED (${why})` };
  } finally {
    clearTimeout(timer);
  }
};

const networkLine = () => {
  const c = (navigator as any).connection;
  const parts = [navigator.onLine ? 'online' : 'OFFLINE (browser flag)'];
  if (c) {
    if (c.effectiveType) parts.push(c.effectiveType);
    if (c.downlink) parts.push(`~${c.downlink} Mbps`);
    if (c.rtt !== undefined) parts.push(`rtt ${c.rtt} ms`);
    if (c.saveData) parts.push('data saver on');
  }
  return `network: ${parts.join(', ')}`;
};

export type ChunkDiagnosis = {
  /** Plain-English, multi-line explanation (verdict first). */
  diagnosis: string;
  verdict: string;
  chunks: string;
  waitedMs: number;
  hiddenDuringWait: boolean;
  pendingChunkDownloads: number;
  deployChanged?: boolean;
};

/** Gather evidence for a failed lazy chunk import. Never throws. */
export const diagnoseChunkFailure = async (
  factory: () => unknown,
  err: any,
  startedAt: number,
  timeoutMs: number
): Promise<ChunkDiagnosis> => {
  try {
    const waitedMs = Date.now() - startedAt;
    const hiddenDuringWait = wasHiddenSince(startedAt);
    const ids = chunkIdsOf(factory);
    const files = ids
      .flatMap((id) => [describeChunkFile(id, 'js', startedAt), describeChunkFile(id, 'css', startedAt)])
      .filter(Boolean) as ChunkFileState[];
    const pendingChunkDownloads = document.querySelectorAll('script[data-webpack]').length;
    const probe = await probeManifest(ids);

    const pending = files.filter((f) => f.state === 'pending');
    const failed = files.filter((f) => f.state === 'failed');
    const done = files.filter((f) => f.state === 'done');

    // Most specific explanation first.
    let verdict: string;
    if (hiddenDuringWait || waitedMs > timeoutMs + 5000) {
      verdict =
        `Timer fired late: the tab was in the background or the computer was asleep during the wait ` +
        `(waited ${secs(waitedMs)} for a ${secs(timeoutMs)} limit). Browsers pause background tabs, ` +
        `so this is most likely NOT a network or server problem.`;
    } else if (!navigator.onLine) {
      verdict = 'Browser was offline, so the screen file could not be downloaded.';
    } else if (!probe.ok) {
      verdict =
        'The site itself did not answer a direct check either — the network or the web server/CDN ' +
        'was unreachable at that moment.';
    } else if (probe.deployChanged || probe.missingChunks?.length) {
      verdict =
        'A newer version of the app was deployed while this page was open; the old screen file ' +
        'it asked for no longer exists. A refresh loads the new version.';
    } else if (failed.length) {
      verdict = `The server returned an error for the screen file (${failed.map((f) => `${f.id}.${f.kind}`).join(', ')}).`;
    } else if (pending.length) {
      verdict =
        `The screen file (${pending.map((f) => `${f.id}.${f.kind}`).join(', ')}) was still downloading ` +
        `after ${secs(waitedMs)} while the site answered a direct check in ${probe.ms} ms — that one ` +
        `download stalled (slow link, proxy/antivirus scanning, or queued behind ` +
        `${pendingChunkDownloads} other chunk download(s)).`;
    } else if (done.length && done.length === files.length) {
      verdict =
        'All screen files did arrive, but only after the time limit — a slow connection rather ' +
        'than a failure.';
    } else {
      verdict = 'Cause unclear from the browser side — see the evidence below.';
    }

    const errKind = err?.name === 'ChunkLoadError'
      ? `ChunkLoadError (webpack: type=${err?.type ?? '?'}, request=${err?.request ?? '?'})`
      : String(err?.message || err);
    const visibility = hiddenDuringWait
      ? `tab was HIDDEN at some point during the wait (now ${document.visibilityState})`
      : 'tab visible throughout';
    const sw = navigator.serviceWorker?.controller ? 'active (may be serving cached files)' : 'none';

    const lines = [
      `verdict: ${verdict}`,
      `error: ${errKind}`,
      `waited: ${secs(waitedMs)} (limit ${secs(timeoutMs)}); ${visibility}`,
      `screen files (${ids.length ? ids.join(', ') : 'unknown ids'}):`,
      ...(files.length ? files.map((f) => `- ${f.line}`) : ['- no chunk ids could be read']),
      `other chunk downloads in flight: ${pendingChunkDownloads}`,
      probe.line,
      networkLine(),
      `service worker: ${sw}`
    ];

    return {
      diagnosis: lines.join('\n'),
      verdict,
      chunks: files.map((f) => `${f.id}.${f.kind}:${f.state}`).join(' '),
      waitedMs,
      hiddenDuringWait,
      pendingChunkDownloads,
      deployChanged: probe.deployChanged
    };
  } catch (e: any) {
    const msg = `diagnosis failed: ${String(e?.message || e)}`;
    return {
      diagnosis: msg,
      verdict: msg,
      chunks: '',
      waitedMs: Date.now() - startedAt,
      hiddenDuringWait: false,
      pendingChunkDownloads: 0
    };
  }
};

// ── Carry the first attempt across the automatic reload ─────────────────────
// The first failure reloads the page and only a SECOND failure reaches the
// recovery screen (and the log). Keep the first attempt's diagnosis so the
// crash entry explains both.

export const rememberFailedAttempt = (d: ChunkDiagnosis) => {
  try {
    sessionStorage.setItem(
      PREVIOUS_ATTEMPT_KEY,
      JSON.stringify({ at: Date.now(), page: window.location.pathname, diagnosis: d.diagnosis })
    );
  } catch {
    /* storage full / disabled — the second attempt still logs itself */
  }
};

/** The previous attempt's diagnosis as text (and clears it), or undefined. */
export const takePreviousAttempt = (): string | undefined => {
  try {
    const raw = sessionStorage.getItem(PREVIOUS_ATTEMPT_KEY);
    if (!raw) return undefined;
    sessionStorage.removeItem(PREVIOUS_ATTEMPT_KEY);
    const prev = JSON.parse(raw);
    // A leftover from a reload that then succeeded — not part of this failure.
    if (!(Date.now() - Number(prev.at) < PREVIOUS_ATTEMPT_MAX_AGE_MS)) return undefined;
    return [
      `${secs(Date.now() - Number(prev.at))} before this, on ${prev.page}, the first attempt failed and the page auto-reloaded:`,
      String(prev.diagnosis || '')
    ].join('\n');
  } catch {
    return undefined;
  }
};
